import React, { useState, useEffect } from 'react';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, styled } from "@mui/material/styles";
import { useAuth } from "../../context/AuthContext";
import { getAllSubUsers } from "../../api/subUserApi";
import { getAllApprovalMatrix, addApprovalMatrix, updateApprovalMatrix, deleteApprovalMatrix } from "../../api/approvalMatrixApi";
import "../../styles.css";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha("#f2f1f1", 1),
  "&:hover": {
    backgroundColor: alpha("#f2f1f1", 0.85),
  },
  marginRight: "16px",
  width: "100%",
  maxWidth: "400px",
  border: "1px solid #39aafc",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  fontSize: "13px",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
  },
}));

const ApprovalMatrix = ({ accessLevel }) => {
  const { user } = useAuth();
  const isMobile = useMediaQuery("(max-width:900px)");
  const isMainUser = user?.user_type === 'main';
  const isViewOnly = !isMainUser && accessLevel === 'view_only';
  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedPersonIncharge, setSelectedPersonIncharge] = useState(null);
  const [matrixData, setMatrixData] = useState([]);
  const [loading, setLoading] = useState(false);

  // While the add/edit sheet is open on mobile, hide the floating pill nav
  useEffect(() => {
    const open = isMobile && openDialog;
    document.body.classList.toggle("arcdx-sheet-open", open);
    return () => document.body.classList.remove("arcdx-sheet-open");
  }, [isMobile, openDialog]);

  // Fetch employees from database
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        if (!user?.company_id) return;

        const response = await getAllSubUsers({
          company_id: user.company_id,
          user_status: 'active'
        });

        if (response.success) {
          const employees = response.data.map(emp => ({
            id: emp._id,
            name: emp.fullname
          }));
          // Main-account users are never sub-users, so they'd otherwise never
          // appear in this list — add "you" explicitly so the logged-in user
          // can be picked as Employee or Person Incharge too.
          const withCurrentUser = user?._id
            ? [{ id: user._id, name: user.fullname || "You", isCurrentUser: true }, ...employees]
            : employees;
          setEmployeeOptions(withCurrentUser);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };

    fetchEmployees();
  }, [user?.company_id, user?._id, user?.fullname]);

  // Fetch approval matrix data from database
  useEffect(() => {
    fetchMatrixData();
  }, [user?.company_id]);

  const fetchMatrixData = async () => {
    try {
      if (!user?.company_id) return;
      setLoading(true);

      const response = await getAllApprovalMatrix({
        company_id: user.company_id
      });

      if (response.success) {
        const data = response.data.map(item => ({
          id: item._id,
          employeeId: item.employee_id,
          employeeName: item.employee_name,
          personInchargeId: item.person_incharge_id,
          personIncharge: item.person_incharge
        }));
        setMatrixData(data);
      }
    } catch (error) {
      console.error("Error fetching approval matrix:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Shows "(You)" next to the logged-in user's entry in the dropdown list only
  // — getOptionLabel stays clean so the saved employee_name has no suffix.
  const renderEmployeeOption = (props, option) => (
    <li {...props} key={option.id}>
      {option.name}{option.isCurrentUser ? " (You)" : ""}
    </li>
  );

  const handleMatrixClick = () => {
    setIsEditMode(false);
    setEditingId(null);
    setSelectedEmployee(null);
    setSelectedPersonIncharge(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEditMode(false);
    setEditingId(null);
    setSelectedEmployee(null);
    setSelectedPersonIncharge(null);
  };

  const handleAddMatrix = async () => {
    if (!selectedEmployee || !selectedPersonIncharge) {
      alert("Please fill all fields");
      return;
    }

    try {
      const matrixPayload = {
        employee_id: selectedEmployee.id,
        employee_name: selectedEmployee.name,
        person_incharge_id: selectedPersonIncharge.id,
        person_incharge: selectedPersonIncharge.name
      };

      if (isEditMode && editingId) {
        // Update existing entry
        const response = await updateApprovalMatrix(editingId, matrixPayload);
        if (response.success) {
          fetchMatrixData();
          handleCloseDialog();
        }
      } else {
        // Add new entry
        const response = await addApprovalMatrix(matrixPayload);
        if (response.success) {
          fetchMatrixData();
          handleCloseDialog();
        }
      }
    } catch (error) {
      console.error("Error saving approval matrix:", error);
      alert("Error saving approval matrix entry");
    }
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setEditingId(item.id);

    // Find the employee and person incharge from options
    const employee = employeeOptions.find(emp => emp.id === item.employeeId) ||
                     { id: item.employeeId, name: item.employeeName };
    const personIncharge = employeeOptions.find(emp => emp.id === item.personInchargeId) ||
                          { id: item.personInchargeId, name: item.personIncharge };

    setSelectedEmployee(employee);
    setSelectedPersonIncharge(personIncharge);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) {
      return;
    }

    try {
      const response = await deleteApprovalMatrix(id);
      if (response.success) {
        fetchMatrixData();
      }
    } catch (error) {
      console.error("Error deleting approval matrix:", error);
      alert("Error deleting approval matrix entry");
    }
  };

  // Filter data based on search
  const filteredData = matrixData.filter(item =>
    item.employeeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.personIncharge.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      {/* ===== MOBILE: approval chain ===== */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <div className="arcdx-v-search" style={{ marginBottom: 12 }}>
          <SearchIcon sx={{ fontSize: 20, color: "#98a2b3" }} />
          <input placeholder="Search approvals…" value={searchQuery} onChange={handleSearchChange} />
        </div>
        {loading ? (
          <div className="arcdx-v-empty">Loading…</div>
        ) : filteredData.length === 0 ? (
          <div className="arcdx-v-empty">
            <div className="big">🪜</div>
            No approval chains yet
            <Box sx={{ mt: 0.5, fontSize: "12px", color: "#b9c0cc" }}>
              Map each employee to their approver below
            </Box>
          </div>
        ) : (
          filteredData.map((item) => (
            <div key={item.id} className="arcdx-v-card" onClick={() => !isViewOnly && handleEdit(item)}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Employee
                  </Box>
                  <Box sx={{ fontSize: "14px", fontWeight: 700, color: "#101828", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {item.employeeName}
                  </Box>
                </Box>
                <Box sx={{ color: "#39aafc", fontSize: "18px", fontWeight: 800, px: "4px" }}>→</Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Approver
                  </Box>
                  <Box sx={{ fontSize: "14px", fontWeight: 700, color: "#1f6fd6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {item.personIncharge}
                  </Box>
                </Box>
                {!isViewOnly && (
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                    sx={{ color: "#d0d5dd", "&:active": { color: "#d92d20" } }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Box>
            </div>
          ))
        )}
        {!isViewOnly && (
          <button type="button" className="arcdx-v-addbtn" onClick={handleMatrixClick}>
            + Add Approval Chain
          </button>
        )}
      </Box>

      {/* Search Bar and Matrix Button */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Search
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e4e7ec",
            borderRadius: "13px",
            transition: "border-color 0.18s ease, box-shadow 0.18s ease",
            "&:hover": { backgroundColor: "#ffffff" },
            "&:focus-within": { borderColor: "#39aafc", boxShadow: "0 0 0 4px rgba(57,170,252,0.12)" },
          }}
        >
          <SearchIconWrapper>
            <SearchIcon sx={{ fontSize: 19, color: "#98a2b3" }} />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="Search…"
            inputProps={{ "aria-label": "search" }}
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </Search>

        {!isViewOnly && (
        <Button
          variant="contained"
          onClick={handleMatrixClick}
          sx={{
            marginLeft: "auto",
            background: "linear-gradient(135deg, #4db2fc, #1565C0)",
            color: "white",
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 700,
            minWidth: "120px",
            height: "40px",
            borderRadius: "13px",
            boxShadow: "0 8px 20px rgba(21, 101, 192, 0.28)",
            "&:hover": {
              background: "linear-gradient(135deg, #39aafc, #0d47a1)",
              boxShadow: "0 10px 24px rgba(21, 101, 192, 0.38)",
            },
          }}
        >
          Add Matrix
        </Button>
        )}
      </Box>

      {/* Approval Matrix Table */}
      <TableContainer component={Paper} sx={{ display: { xs: "none", md: "block" }, boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)", borderRadius: "16px", border: "1px solid #eef2f6", overflow: "hidden" }}>
        <Table sx={{ minWidth: 650, "& tbody tr:hover": { backgroundColor: "#f8fbff" } }} aria-label="approval matrix table">
          <TableHead>
            <TableRow>
              <TableCell className="table-head-cell" sx={{ width: "60px", textAlign: "center" }}>
                S/No
              </TableCell>
              <TableCell className="table-head-cell" sx={{ width: "250px" }}>
                Name of the Employee
              </TableCell>
              <TableCell className="table-head-cell" sx={{ width: "250px" }}>
                Person Incharge
              </TableCell>
              <TableCell className="table-head-cell" sx={{ width: "120px", textAlign: "center" }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: "center", padding: "40px", fontSize: "13px", color: "#666" }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: "center", padding: "40px", fontSize: "13px", color: "#666" }}>
                  No Records Found
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell className="table-body-cell" sx={{ textAlign: "center" }}>
                    {index + 1}
                  </TableCell>
                  <TableCell className="table-body-cell">
                    {item.employeeName}
                  </TableCell>
                  <TableCell className="table-body-cell">
                    {item.personIncharge}
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "center" }}>
                    {isViewOnly ? (
                      "—"
                    ) : (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(item)}
                          sx={{ color: "#98a2b3", "&:hover": { color: "#1f6fd6" } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(item.id)}
                          sx={{ color: "#d0d5dd", "&:hover": { color: "#d92d20" } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Matrix Dialog (desktop; mobile uses the bottom sheet) */}
      <Dialog
        open={!isMobile && openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "17px",
            fontWeight: 800,
            fontFamily: "Roboto, Arial, sans-serif",
            background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)",
            color: "#fff",
            padding: "18px 24px"
          }}
        >
          {isEditMode ? "Edit Matrix" : "Add Matrix"}
          <IconButton onClick={handleCloseDialog} size="small" sx={{ color: "#fff", background: "rgba(255,255,255,0.15)", "&:hover": { background: "rgba(255,255,255,0.25)" } }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "24px !important", paddingTop: "24px !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {/* Employee Name */}
            <Autocomplete
              options={employeeOptions}
              getOptionLabel={(option) => option.name || ""}
              renderOption={renderEmployeeOption}
              value={selectedEmployee}
              onChange={(event, newValue) => setSelectedEmployee(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employee Name"
                  variant="outlined"
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: "13px",
                      fontFamily: "Roboto, Arial, sans-serif",
                      height: "48px",
                      borderRadius: "12px",
                      backgroundColor: "#f9fafb",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "13px",
                      fontFamily: "Roboto, Arial, sans-serif"
                    },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e4e7ec" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc", borderWidth: "1.5px" },
                  }}
                />
              )}
            />

            {/* Person Incharge */}
            <Autocomplete
              options={employeeOptions}
              getOptionLabel={(option) => option.name || ""}
              renderOption={renderEmployeeOption}
              value={selectedPersonIncharge}
              onChange={(event, newValue) => setSelectedPersonIncharge(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Person Incharge"
                  variant="outlined"
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: "13px",
                      fontFamily: "Roboto, Arial, sans-serif",
                      height: "48px",
                      borderRadius: "12px",
                      backgroundColor: "#f9fafb",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "13px",
                      fontFamily: "Roboto, Arial, sans-serif"
                    },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e4e7ec" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc", borderWidth: "1.5px" },
                  }}
                />
              )}
            />

            {/* Add/Update Matrix Button */}
            <Button
              variant="contained"
              onClick={handleAddMatrix}
              sx={{
                background: "linear-gradient(135deg, #4db2fc, #1565C0)",
                color: "white",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 800,
                height: "44px",
                borderRadius: "14px",
                marginTop: "10px",
                boxShadow: "0 8px 20px rgba(21, 101, 192, 0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #39aafc, #0d47a1)",
                },
              }}
            >
              {isEditMode ? "Update Matrix" : "Add Matrix"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ===== MOBILE SHEET: Add / Edit Approval Chain ===== */}
      {isMobile && openDialog && (() => {
        const vIn = { "& .MuiOutlinedInput-root": { borderRadius: "14px", backgroundColor: "#f9fafb", fontSize: "15px", "& fieldset": { borderColor: "#e4e7ec" }, "&.Mui-focused fieldset": { borderColor: "#39aafc", borderWidth: "1.5px" } }, "& .MuiInputBase-input": { fontSize: "15px", padding: "13px 14px" }, "& .MuiInputLabel-root": { fontSize: "13px" } };
        return (
          <>
            <div className="arcdx-sheet-back" onClick={handleCloseDialog} />
            <div className="arcdx-sheet">
              <div className="arcdx-sheet-grip" />
              <div className="arcdx-v-sheet-head">
                <span className="arcdx-v-sheet-title">{isEditMode ? "Edit Approval Chain" : "New Approval Chain"}</span>
                <IconButton onClick={handleCloseDialog} size="small" sx={{ background: "#f2f4f7" }}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </div>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <Autocomplete
                  options={employeeOptions}
                  getOptionLabel={(o) => o.name || ""}
                  renderOption={renderEmployeeOption}
                  value={selectedEmployee}
                  onChange={(e, v) => setSelectedEmployee(v)}
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  renderInput={(p) => <TextField {...p} label="Employee" sx={vIn} />}
                />
                <Autocomplete
                  options={employeeOptions}
                  getOptionLabel={(o) => o.name || ""}
                  renderOption={renderEmployeeOption}
                  value={selectedPersonIncharge}
                  onChange={(e, v) => setSelectedPersonIncharge(v)}
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  renderInput={(p) => <TextField {...p} label="Person Incharge (approver)" sx={vIn} />}
                />
                <Button
                  onClick={handleAddMatrix}
                  sx={{ py: 1.5, borderRadius: "14px", background: "linear-gradient(135deg,#39aafc,#1f8fe8)", color: "#fff", fontWeight: 700, textTransform: "none", fontSize: "16px", boxShadow: "0 8px 20px rgba(31,143,232,0.4)" }}
                  fullWidth
                >
                  {isEditMode ? "Save Changes" : "Add Chain"}
                </Button>
              </Box>
            </div>
          </>
        );
      })()}
    </Box>
  );
};

export default ApprovalMatrix;
